import React, { useEffect, useRef } from "react";
import {
  ShoppingBag,
  Image,
  Star,
  Info,
  ClipboardList,
  UtensilsCrossed,
  LucideIcon,
} from "lucide-react";
import "./styles/BusinessTabBar.css";

export type BusinessTab =
  | "products"
  | "gallery"
  | "reviews"
  | "more"
  | "orders"
  | "dinein";

interface Tab {
  id: BusinessTab;
  label: string;
  Icon: LucideIcon;
}

const BASE_TABS: Tab[] = [
  { id: "products", label: "Products", Icon: ShoppingBag },
  { id: "gallery",  label: "Gallery",  Icon: Image },
  { id: "reviews",  label: "Reviews",  Icon: Star },
  { id: "more",     label: "More",     Icon: Info },
];

const ORDERS_TAB: Tab = { id: "orders", label: "Orders", Icon: ClipboardList };
/** Same icon as the "Ordering for Table N" banner on this page, so staff read
 *  the two as the same feature rather than two unrelated dine-in things. */
const DINE_IN_TAB: Tab = { id: "dinein", label: "Dine-in", Icon: UtensilsCrossed };

interface BusinessTabBarProps {
  activeTab: BusinessTab;
  onTabChange: (tab: BusinessTab) => void;
  /** Show the Orders tab (NineFinds ordering enabled for this business). */
  showOrders?: boolean;
  /** Show the Dine-in kitchen board (staff-only — see BusinessListings). */
  showDineIn?: boolean;
}

/**
 * Four tabs for most businesses, five with Orders, six with the Dine-in board
 * as well. The track scrolls horizontally when they don't fit (see the CSS),
 * and each tab draws its own underline, so variable tab widths need no
 * measuring.
 */
const BusinessTabBar: React.FC<BusinessTabBarProps> = ({
  activeTab,
  onTabChange,
  showOrders = false,
  showDineIn = false,
}) => {
  const tabs = [
    ...BASE_TABS,
    ...(showOrders ? [ORDERS_TAB] : []),
    ...(showDineIn ? [DINE_IN_TAB] : []),
  ];

  const trackRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Partial<Record<BusinessTab, HTMLButtonElement | null>>>({});

  // Keep the active tab visible when the bar scrolls (e.g. a ?tab=dinein deep
  // link on a narrow phone). Deliberately NOT scrollIntoView — that also
  // scrolls ancestors, and this bar is sticky, so it would yank the whole page
  // vertically on every tab change. Scrolling the track only moves the track.
  useEffect(() => {
    const track = trackRef.current;
    const el = tabRefs.current[activeTab];
    if (!track || !el) return;
    if (track.scrollWidth <= track.clientWidth) return; // nothing to scroll
    track.scrollTo({
      left: Math.max(0, el.offsetLeft - (track.clientWidth - el.offsetWidth) / 2),
      behavior: "smooth",
    });
  }, [activeTab, tabs.length]);

  return (
    <div className="biz-tabbar">
      <div className="biz-tabbar__track" ref={trackRef} role="tablist">
        {tabs.map(({ id, label, Icon }) => (
          <button
            key={id}
            ref={(node) => {
              tabRefs.current[id] = node;
            }}
            className={`biz-tabbar__tab ${activeTab === id ? "biz-tabbar__tab--active" : ""}`}
            onClick={() => onTabChange(id)}
            aria-selected={activeTab === id}
            role="tab"
          >
            <Icon size={18} strokeWidth={activeTab === id ? 2.5 : 2} />
            <span className="biz-tabbar__label">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default BusinessTabBar;
